import { ref, computed } from 'vue'
import axios from 'axios'

const apiUrl = import.meta.env.VITE_URL_API
const apiKey = import.meta.env.VITE_API_KEY

// Fallbacks used until (or if) the manufacturer branding loads
const DEFAULT_ACCENT_COLOR = '#2563eb'
const DEFAULT_UI_GRADIENT_1 = '#1a2c50'
const DEFAULT_UI_GRADIENT_2 = '#0b1526'

// The photo checklist shown to the customer. Categories are client-side only;
// the upload endpoint doesn't support tagging, so we encode the category in the file name.
export const PHOTO_CATEGORIES = [
  { id: 'front', label: 'Front', description: 'Front of the vehicle', icon: 'car', hint: 'Stand back so the whole front is in frame' },
  {
    id: 'back',
    label: 'Back',
    description: 'Back of the vehicle',
    icon: 'car-rear',
    hint: 'Capture the full rear, including the number plate'
  },
  {
    id: 'left-side',
    label: 'Left side',
    description: 'Left side of the vehicle',
    icon: 'car-side',
    hint: 'Capture the whole left side, from bumper to bumper'
  },
  {
    id: 'right-side',
    label: 'Right side',
    description: 'Right side of the vehicle',
    icon: 'car-side',
    hint: 'Capture the whole right side, from bumper to bumper'
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Dashboard (incl. mileage)',
    icon: 'gauge-high',
    hint: 'Turn the ignition on so the mileage is visible'
  },
  {
    id: 'interior-boot',
    label: 'Interior – Boot',
    description: 'Inside of the boot',
    icon: 'box-open',
    hint: 'Open the boot and capture the full load space'
  },
  { id: 'interior-rear', label: 'Interior – Rear', description: 'Rear seats', icon: 'couch', hint: 'Capture the rear seats and footwells' },
  {
    id: 'interior-front',
    label: 'Interior – Front',
    description: 'Front seats',
    icon: 'car-mirrors',
    hint: 'Capture the front seats and centre console'
  },
  { id: 'engine', label: 'Engine', description: 'Engine bay', icon: 'engine', hint: 'Open the bonnet and capture the engine bay' },
  {
    id: 'wheel-front-left',
    label: 'Front left wheel',
    description: 'Front left wheel & tyre',
    icon: 'tire',
    hint: 'Capture the wheel clearly, showing the alloy and tyre'
  },
  {
    id: 'wheel-front-right',
    label: 'Front right wheel',
    description: 'Front right wheel & tyre',
    icon: 'tire',
    hint: 'Capture the wheel clearly, showing the alloy and tyre'
  },
  {
    id: 'wheel-rear-left',
    label: 'Rear left wheel',
    description: 'Rear left wheel & tyre',
    icon: 'tire',
    hint: 'Capture the wheel clearly, showing the alloy and tyre'
  },
  {
    id: 'wheel-rear-right',
    label: 'Rear right wheel',
    description: 'Rear right wheel & tyre',
    icon: 'tire',
    hint: 'Capture the wheel clearly, showing the alloy and tyre'
  }
]

// Damage photos are captured in their own flow, after the main categories
export const DAMAGE_CATEGORY = {
  id: 'damage',
  label: 'Damage close up',
  description: 'Close ups of any damage',
  icon: 'magnifying-glass',
  hint: 'Get close to any scratches, dents or scuffs'
}

export function valuationUploadEndpoint(valuationId) {
  return `${apiUrl}/v1/valuations/${valuationId}/upload-images?api_key=${apiKey}`
}

export function useValuation(valuationId) {
  const valuation = ref(null)
  const agreement = ref(null)
  const isLoading = ref(true)
  const error = ref(null)

  const hasUploadedImages = computed(() => {
    const v = valuation.value
    if (!v) return false
    return Boolean(v.isImageUploaded) || (Array.isArray(v.images) && v.images.length > 0)
  })

  const uploadedImageCount = computed(() => {
    return Array.isArray(valuation.value?.images) ? valuation.value.images.length : null
  })

  const vehicle = computed(() => agreement.value?.vehicle || valuation.value?.vehicle || null)
  const dealership = computed(() => agreement.value?.dealership || null)
  const customerForename = computed(() => agreement.value?.customer?.forename || null)

  function applyBrandColor() {
    const brandColor = dealership.value?.manufacturerBrandColor || DEFAULT_ACCENT_COLOR
    const gradient1 = dealership.value?.manufacturerUiGradient1 || DEFAULT_UI_GRADIENT_1
    const gradient2 = dealership.value?.manufacturerUiGradient2 || DEFAULT_UI_GRADIENT_2
    document.documentElement.style.setProperty('--manufacturer-color', brandColor)
    document.documentElement.style.setProperty('--manufacturer-gradient-1', gradient1)
    document.documentElement.style.setProperty('--manufacturer-gradient-2', gradient2)
  }

  async function fetchValuation() {
    const response = await axios.get(`${apiUrl}/v1/valuations/${valuationId}`, { params: { api_key: apiKey } })
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to load your valuation')
    }
    valuation.value = response.data.data
  }

  async function fetchAgreement(agreementId) {
    const response = await axios.get(`${apiUrl}/v1/agreements/${agreementId}`, { params: { api_key: apiKey } })
    if (response.data.success) {
      agreement.value = response.data.data
    }
  }

  async function load() {
    if (!valuationId) {
      error.value = 'Invalid or missing valuation ID'
      isLoading.value = false
      return
    }

    isLoading.value = true
    error.value = null
    applyBrandColor()

    try {
      await fetchValuation()
      if (valuation.value?.agreementId) {
        await fetchAgreement(valuation.value.agreementId)
      }
      applyBrandColor()
    } catch (e) {
      console.error(`Failed to load valuation: ${e.message}`)
      error.value = "We couldn't load your valuation. Please check your link and try again."
    } finally {
      isLoading.value = false
    }
  }

  async function refreshValuation() {
    try {
      await fetchValuation()
    } catch (e) {
      console.error(`Failed to refresh valuation: ${e.message}`)
    }
  }

  return {
    valuation,
    agreement,
    vehicle,
    dealership,
    customerForename,
    isLoading,
    error,
    hasUploadedImages,
    uploadedImageCount,
    load,
    refreshValuation
  }
}
